'use client'

import { useState, useCallback } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { ContentContainer } from '@/components/ui/content-container'
import { cn } from '@/lib/utils'
import {
  trackInitiateCheckout,
  trackPathSelected,
  trackViewSampleBrief,
} from '@/lib/analytics'

/* ─── DATA ─── */

const QUESTIONS = [
  {
    q: 'What do we get for free?',
    a: 'You and your partner answer one conversation privately and receive one written Brief. No payment information required. The full Check-In is a one-time $29 payment and unlocks all four Briefs and your Marriage Roadmap.',
  },
  {
    q: 'Will my partner see my answers?',
    a: 'Not while either of you is answering. After both of you finish, you receive the same written Brief, which may name where your answers align and differ.',
  },
  {
    q: 'Can I start if my partner is not ready?',
    a: "Yes. You can start one conversation and invite your partner when you\u2019re ready. Your Brief appears after both of you answer.",
  },
  {
    q: 'Is this therapy?',
    a: 'No. The Marriage Check-In is not therapy, counseling, or professional advice. It is a structured way to answer privately, see patterns, and start one better conversation.',
  },
]

/* ─── ANIMATION ─── */

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0 },
}

const viewport = { once: true, margin: '-80px' }

/* ─── HOOK: track hero CTA visibility ─── */

function useCTAVisibility() {
  const [pastHero, setPastHero] = useState(false)

  const observe = useCallback((id: string, el: HTMLElement | null) => {
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (id === 'hero' && !entry.isIntersecting) setPastHero(true)
        if (id === 'hero' && entry.isIntersecting) setPastHero(false)
      },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const showSticky = pastHero

  return { observe, showSticky }
}

/* ─── COMPONENTS ─── */


function QuestionItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false)

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      transition={{ duration: 0.4, ease: 'easeOut', delay: index * 0.05 }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full text-left rounded-[18px] border border-slate-faint px-4 py-3.5 transition-all duration-200 active:scale-[0.99]",
          open ? "bg-white/70 border-copper/25" : "bg-white/45 hover:bg-white/60"
        )}
        aria-expanded={open}
      >
        <div className="flex items-start justify-between gap-3">
          <span className="text-[16px] font-semibold text-ink leading-snug">{q}</span>
          <span
            className={cn(
              'mt-0.5 shrink-0 text-[17px] text-copper/70 transition-transform duration-200',
              open ? 'rotate-45' : 'rotate-0'
            )}
            aria-hidden="true"
          >
            +
          </span>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="overflow-hidden"
            >
              <p className="mt-3 text-[15px] leading-6 text-ink-muted pr-8">
                {a}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </motion.div>
  )
}

/* ─── PAGE ─── */

export default function LandingClient() {
  const { observe, showSticky } = useCTAVisibility()
  const [pathSheetOpen, setPathSheetOpen] = useState(false)

  const heroCTARef = useCallback((el: HTMLDivElement | null) => observe('hero', el), [observe])

  return (
    <div className="min-h-screen bg-paper text-ink">
      <div className="noise-overlay" aria-hidden="true" />

      {/* Sticky header */}
      <header className="safe-top sticky top-0 z-40 border-b border-slate-faint/70 bg-paper/86 backdrop-blur-xl">
        <ContentContainer size="wide" className="flex h-12 items-center justify-between gap-3">
          <Link
            href="/"
            className="min-w-0 truncate font-serif text-[18px] leading-none tracking-tight text-ink"
          >
            The Marriage Check-In
          </Link>

          <div className="flex items-center justify-end min-w-[92px]">
            {showSticky ? (
              <button
                type="button"
                onClick={() => {
                  trackInitiateCheckout('sticky_header');
                  setPathSheetOpen(true);
                }}
                aria-label="Start one conversation free"
                className="inline-flex h-9 items-center justify-center rounded-full bg-ink px-4 text-[13px] font-semibold text-white transition-all active:scale-[0.98]"
              >
                Start free
              </button>
            ) : (
              <Link
                href="/sign-in?redirectTo=/dashboard"
                className="flex min-h-[44px] items-center px-2 -mr-2 text-[14px] font-semibold text-ink-muted transition-colors hover:text-copper"
              >
                Sign in
              </Link>
            )}
          </div>
        </ContentContainer>
      </header>

      <main className="pb-16 sm:pb-20">

        {/* ── HERO ── */}
        <section className="relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-80"
            style={{
              background:
                'radial-gradient(circle at 18% 18%, rgba(168,88,50,0.15), transparent 28%), radial-gradient(circle at 82% 0%, rgba(240,226,214,0.9), transparent 32%)',
            }}
            aria-hidden="true"
          />

          <ContentContainer size="wide" className="relative pt-8 pb-4 sm:pt-14 sm:pb-8 lg:pt-18">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="inline-flex items-center gap-2 rounded-full border border-copper/20 bg-white/50 px-3 py-1.5 text-[12px] font-semibold text-copper shadow-[0_10px_28px_-26px_rgba(26,26,26,0.4)]"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-copper" />
                Private couples check-in
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, ease: 'easeOut', delay: 0.08 }}
                className="mt-4 font-serif text-[2.6rem] leading-[0.88] tracking-[-0.04em] text-ink sm:text-[4.6rem] lg:text-[5.4rem]"
              >
                A private check-in for the conversations you&apos;ve been putting off.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, ease: 'easeOut', delay: 0.18 }}
                className="mt-4 max-w-[30rem] text-[16px] leading-6 text-ink-soft sm:text-[18px] sm:leading-7"
              >
                You and your partner answer privately. Then you both get one written Brief: where you line up, where you don&apos;t, and what to talk about next.
              </motion.p>

              <motion.div
                ref={heroCTARef}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, ease: 'easeOut', delay: 0.28 }}
                className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center"
              >
                <button
                  type="button"
                  onClick={() => {
                    trackInitiateCheckout('hero');
                    setPathSheetOpen(true);
                  }}
                  className="inline-flex min-h-[56px] w-full items-center justify-center rounded-full bg-ink px-6 text-[15px] font-semibold text-white shadow-[0_18px_38px_-18px_rgba(26,26,26,0.55)] transition-all duration-200 active:translate-y-[1px] active:scale-[0.99] sm:w-auto"
                >
                  Start one conversation free
                </button>
                <Link
                  href="#preview"
                  onClick={() => trackViewSampleBrief()}
                  className="inline-flex items-center gap-1.5 min-h-[44px] text-[14px] font-semibold text-ink-muted transition-colors hover:text-copper"
                >
                  See a sample Brief <span className="text-copper">&darr;</span>
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, ease: 'easeOut', delay: 0.38 }}
                className="mt-4 flex gap-2 text-[12px] text-ink-muted"
              >
                <span className="rounded-full bg-white/65 border border-white/70 px-3 py-1.5 text-[12px] text-ink-muted">10–15 minutes</span>
                <span className="rounded-full bg-white/65 border border-white/70 px-3 py-1.5 text-[12px] text-ink-muted">Private answers</span>
                <span className="rounded-full bg-white/65 border border-white/70 px-3 py-1.5 text-[12px] text-ink-muted">No score</span>
              </motion.div>


            </div>
          </ContentContainer>
        </section>

        {/* ── BRIEF PREVIEW ── */}
        <section id="preview" className="scroll-mt-16 pt-12 sm:pt-18">
          <ContentContainer size="wide">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              className="rounded-[1.5rem] border border-copper/15 bg-copper-faint/70 p-5 sm:p-7"
            >
              {/* Heading area */}
              <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-copper">What you get</p>
              <h2 className="mt-2 font-serif text-[30px] leading-[0.92] tracking-[-0.035em] text-ink sm:text-[42px]">
                A written Brief built from both of your answers.
              </h2>
              <p className="mt-2 text-[14px] leading-5 text-ink-muted">
                Not a score. It names where you align, where you differ, and what to talk about next.
              </p>

              {/* Sample excerpt card */}
              <div className="mt-5 rounded-2xl border border-copper/10 bg-white/80 p-4 shadow-[0_24px_56px_-42px_rgba(26,26,26,0.5)] border-l-[3px] border-l-[#a85832]">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-copper">Sample excerpt</p>
                <p className="mt-2 font-serif text-[19px] italic leading-[1.18] text-ink-soft">
                  &ldquo;You are not as far apart as this may feel. The difference is in what each of you assumed would happen next.&rdquo;
                </p>
                <p className="mt-2 text-[11px] leading-4 text-ink-faint">
                  Example language. Every Brief is generated from the couple&apos;s answers.
                </p>

                {/* Value block inside card */}
                <div className="mt-4 pt-4 border-t border-slate-faint/60">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-copper">Every Brief includes</p>
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-copper/60" aria-hidden="true" />
                      <p className="text-[13px] leading-5 text-ink-muted">Where you align</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-copper/60" aria-hidden="true" />
                      <p className="text-[13px] leading-5 text-ink-muted">Where you differ</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-copper/60" aria-hidden="true" />
                      <p className="text-[13px] leading-5 text-ink-muted">What pattern may be forming</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-copper/60" aria-hidden="true" />
                      <p className="text-[13px] leading-5 text-ink-muted">The conversation to have next</p>
                    </div>
                  </div>
                </div>

                {/* Full Check-In value inside card */}
                <div className="mt-4 pt-4 border-t border-slate-faint/60">
                  <p className="text-[13px] leading-5 text-ink-muted">
                    <span className="font-semibold text-ink">The full Check-In</span> includes four written Briefs, conversation guides, your Marriage Roadmap, and a downloadable PDF.
                  </p>
                </div>
              </div>

              {/* Start line */}
              <p className="mt-5 text-center text-[14px] text-ink-muted">
                Start with one conversation free.
              </p>
            </motion.div>
          </ContentContainer>
        </section>

        {/* ── DOMAINS ── */}
        <section className="pt-10 sm:pt-12">
          <ContentContainer size="wide">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              className="space-y-4"
            >
              <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-copper">
                Start with one conversation
              </p>

              <p className="max-w-sm text-[18px] leading-7 text-ink-muted">
                Some conversations feel handled until you answer them separately.
              </p>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-1">
                {['Money & Legal', 'Family & Shared Life', 'Commitment & Trust', 'Conflict & Repair'].map((domain) => (
                  <p key={domain} className="text-[15px] font-semibold text-ink">
                    {domain}
                  </p>
                ))}
              </div>
            </motion.div>
          </ContentContainer>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="pt-12 sm:pt-14">
          <ContentContainer size="wide">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-ink-faint">
                How it works
              </p>

              <ol className="mt-5 divide-y divide-slate-faint/70">
                {[
                  'Choose one conversation free.',
                  "Invite your partner when you\u2019re ready.",
                  'Answer separately, 10 to 15 minutes each.',
                  'Get one written Brief to talk through together.',
                ].map((step, i) => (
                  <li key={i} className="flex gap-4 py-3.5">
                    <span className="w-8 shrink-0 text-[13px] font-semibold tracking-[0.08em] text-copper">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <p className="text-[17px] leading-6 text-ink-muted">
                      {step}
                    </p>
                  </li>
                ))}
              </ol>

              <p className="mt-4 text-[15px] text-ink-faint">
                No payment info required.
              </p>
            </motion.div>
          </ContentContainer>
        </section>

        {/* ── TRUST LINE ── */}
        <section className="pt-6 sm:pt-8">
          <ContentContainer size="wide">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              <div className="mx-auto max-w-[340px] rounded-full border border-copper/15 bg-copper/5 px-4 py-2.5 text-center">
                <p className="text-[14px] font-semibold leading-5 text-ink-muted">
                  Not therapy. Not a score. A private check-in.
                </p>
              </div>
            </motion.div>
          </ContentContainer>
        </section>

        {/* ── COMMON QUESTIONS ── */}
        <section className="pt-7 pb-4 sm:pt-10 sm:pb-6">
          <ContentContainer size="wide">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-ink-faint">Common questions</p>
              <h2 className="mt-2 font-serif text-[34px] leading-[0.9] tracking-[-0.035em] text-ink sm:text-[44px]">
                Before you start.
              </h2>
            </motion.div>

            <div className="mt-5 space-y-2.5">
              {QUESTIONS.map((item, index) => (
                <QuestionItem key={item.q} q={item.q} a={item.a} index={index} />
              ))}
            </div>
          </ContentContainer>
        </section>

        {/* ── FINAL CTA ── */}
        <section className="pt-4 pb-8 sm:pt-8 sm:pb-10">
          <ContentContainer size="wide">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              <div className="rounded-[24px] border border-copper/15 bg-copper/5 px-5 py-6 text-center">
                <h2 className="font-serif text-[28px] leading-[0.95] tracking-[-0.03em] text-ink">
                  Start with one conversation free.
                </h2>
                <p className="mt-2 text-[14px] leading-5 text-ink-muted">
                  No payment information required.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    trackInitiateCheckout('final_cta');
                    setPathSheetOpen(true);
                  }}
                  className="mt-5 inline-flex h-14 w-full items-center justify-center rounded-full bg-ink px-6 text-[15px] font-semibold text-white transition-all active:scale-[0.99] sm:w-auto"
                >
                  Start one conversation free
                </button>
              </div>
            </motion.div>
          </ContentContainer>
        </section>

      </main>

      {/* ── FOOTER ── */}
      <footer className="safe-bottom mt-10 relative">
        <div className="mx-auto max-w-[120px] h-px bg-gradient-to-r from-transparent via-copper/30 to-transparent" aria-hidden="true" />
        <div className="border-t border-slate-faint mt-3">
          <ContentContainer size="wide" className="flex flex-col items-center gap-1.5 py-5">
            <p className="font-serif text-[17px] text-ink tracking-tight">The Marriage Check-In</p>
            <div className="flex items-center gap-3 text-[13px] text-ink-faint">
              <a
                href="https://www.instagram.com/themarriagecheckin"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow The Marriage Check-In on Instagram"
                className="hover:text-ink-muted transition-colors"
              >
                Instagram
              </a>
              <span>&middot;</span>
              <a
                href="https://www.tiktok.com/@marriagecheckin"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow The Marriage Check-In on TikTok"
                className="hover:text-ink-muted transition-colors"
              >
                TikTok
              </a>
            </div>
            <div className="flex items-center gap-2 text-[13px] text-ink-faint">
              <Link href="/support" className="hover:text-ink-muted transition-colors">Support</Link>
              <span>&middot;</span>
              <Link href="/privacy" className="hover:text-ink-muted transition-colors">Privacy</Link>
              <span>&middot;</span>
              <Link href="/terms" className="hover:text-ink-muted transition-colors">Terms</Link>
            </div>
            <p className="text-[11px] text-ink-faint mt-1">
              &copy; 2026 Glendale AI, LLC
            </p>
          </ContentContainer>
        </div>
      </footer>

      {/* Path choice bottom sheet */}
      <AnimatePresence>
        {pathSheetOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[60] bg-ink/20 backdrop-blur-[2px]"
              onClick={() => setPathSheetOpen(false)}
              aria-hidden="true"
            />

            {/* Sheet */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="fixed inset-x-0 bottom-0 z-[61] rounded-t-[28px] bg-paper shadow-[0_-18px_60px_-30px_rgba(26,26,26,0.35)]"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
              role="dialog"
              aria-modal="true"
              aria-label="Choose your path"
            >
              <div className="px-5 pt-3 pb-2">
                {/* Handle */}
                <div className="mx-auto h-1 w-10 rounded-full bg-slate-faint" aria-hidden="true" />

                {/* Content */}
                <div className="mt-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-copper">
                    One quick choice
                  </p>
                  <h3 className="mt-2 font-serif text-[28px] leading-[0.95] tracking-[-0.035em] text-ink">
                    Are you getting married or already married?
                  </h3>
                  <p className="mt-2 text-[15px] leading-6 text-ink-muted">
                    Engaged and married couples get different questions.
                  </p>
                </div>

                {/* Path buttons */}
                <div className="mt-5 space-y-2.5">
                  <Link
                    href="/onboarding?path=engaged"
                    className="flex items-center justify-between gap-3 min-h-[56px] w-full rounded-2xl border border-slate-faint bg-white/70 px-5 py-3 text-left transition-all active:scale-[0.99] hover:bg-white/90"
                    onClick={() => {
                      trackPathSelected('engaged');
                      setPathSheetOpen(false);
                    }}
                  >
                    <span className="text-[16px] font-semibold text-ink">We&apos;re getting married</span>
                    <span className="text-[16px] text-copper shrink-0" aria-hidden="true">&rarr;</span>
                  </Link>

                  <Link
                    href="/onboarding?path=married"
                    className="flex items-center justify-between gap-3 min-h-[56px] w-full rounded-2xl border border-slate-faint bg-white/70 px-5 py-3 text-left transition-all active:scale-[0.99] hover:bg-white/90"
                    onClick={() => {
                      trackPathSelected('married');
                      setPathSheetOpen(false);
                    }}
                  >
                    <span className="text-[16px] font-semibold text-ink">We&apos;re already married</span>
                    <span className="text-[16px] text-copper shrink-0" aria-hidden="true">&rarr;</span>
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
